import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Loader2, ArrowLeft, Dumbbell } from "lucide-react";
import { toast } from "sonner";
import { PatientSelector } from "@/components/trainingplan/PatientSelector";
import { TrainingPlanStepper, type WizardStep } from "@/components/trainingplan/wizard/TrainingPlanStepper";
import { HealthProfileStep } from "@/components/trainingplan/wizard/HealthProfileStep";
import { ObjectivesStep, type ObjectiveCard, type CanonicalCategory } from "@/components/trainingplan/wizard/ObjectivesStep";
import {
  AvailabilityStep,
  type AvailabilityState,
  formatAvailabilityForNotes,
} from "@/components/trainingplan/wizard/AvailabilityStep";
import { PlanReviewView } from "@/components/trainingplan/PlanReviewView";
import { PlanPDFTemplate } from "@/components/trainingplan/PlanPDFTemplate";
import { useExportToPDF } from "@/hooks/useExportToPDF";
import type { TrainingPlanHeader, TrainingSession } from "@/components/trainingplan/types";
import type { ActiveAlertInfo } from "@/components/trainingplan/HealthProfileSummaryCard";
import type { Json } from "@/integrations/supabase/types";

type PatientOption = {
  id: string;
  full_name: string;
  cpf: string | null;
  email: string | null;
  data_nascimento?: string | null;
};

type StructuredObjective = {
  title: string;
  category: string;
  reason?: string;
};

const CANONICAL_MAP: Record<string, CanonicalCategory> = {
  hypertrophy: "hypertrophy",
  strength: "hypertrophy",
  fat_loss: "fat_loss",
  cardio_fitness: "cardio_fitness",
  performance: "cardio_fitness",
  mobility: "mobility",
  rehab: "mobility",
  general_health: "general_health",
};

const inferCategory = (title: string): CanonicalCategory => {
  const t = title.toLowerCase();
  if (/hipertrofia|massa muscular|ganhar m(u|ú)sculo|for(c|ç)a|reforço/.test(t)) return "hypertrophy";
  if (/gordura|emagrec|perda de peso|reduzir peso/.test(t)) return "fat_loss";
  if (/cardio|condicionamento|aer(o|ó)bi|f(o|ô)lego|performance|corrida|resist/.test(t))
    return "cardio_fitness";
  if (/reabilit|postura|corretiv|dor|les(a|ã)o|mobilidade|flexibilidade|alongamento/.test(t))
    return "mobility";
  return "general_health";
};

const toObjectiveCards = (raw: StructuredObjective[]): ObjectiveCard[] => {
  return raw
    .map((o) => ({
      title: o.title,
      category: CANONICAL_MAP[o.category] || inferCategory(o.title),
      reason: o.reason,
    }))
    .filter((o, idx, arr) => arr.findIndex((x) => x.title === o.title) === idx);
};

const asStringArray = (value: Json | null | undefined): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const normalizeObjectives = (raw: unknown): StructuredObjective[] => {
  if (!Array.isArray(raw)) return [];
  const out: StructuredObjective[] = [];
  for (const item of raw) {
    if (typeof item === "string") {
      const title = item.trim();
      if (!title) continue;
      out.push({ title, category: inferCategory(title) });
    } else if (isRecord(item)) {
      const title = String(item.title ?? item.name ?? "").trim();
      if (!title) continue;
      const category = typeof item.category === "string" ? item.category : inferCategory(title);
      const reason = typeof item.reason === "string" ? item.reason : undefined;
      out.push({ title, category, reason });
    }
    if (out.length >= 8) break;
  }
  return out;
};

const calcAge = (dob?: string | null): number | null => {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  const diff = Date.now() - d.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
};

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

const FUNCTION_FETCH_ERROR_MESSAGE = "Failed to send a request to the Edge Function";

const buildTrainingPlanWhatsAppMessage = (
  patientName: string,
  publicUrl: string,
  professionalName?: string | null,
) => {
  const lines = [
    `Olá ${patientName}`,
    `Segue seu plano de treino: ${publicUrl}`,
  ];

  if (professionalName) {
    lines.push(`Profissional de saúde: ${professionalName}`);
  }

  lines.push("elinia | Saúde com Inteligência");
  return lines.join("\n");
};

const extractFirstUrl = (text: string): string | null => {
  const match = text.match(/https?:\/\/[^\s]+/i);
  return match?.[0] ?? null;
};

const extractPublicUrlFromWhatsAppUrl = (whatsappUrl: string): string | null => {
  try {
    const parsed = new URL(whatsappUrl);
    const encodedText = parsed.searchParams.get("text");
    if (!encodedText) return null;
    const decoded = decodeURIComponent(encodedText);
    return extractFirstUrl(decoded);
  } catch {
    return null;
  }
};

const buildWhatsAppAppUrl = (whatsappUrl: string): string | null => {
  try {
    const parsedUrl = new URL(whatsappUrl);
    const encodedText = parsedUrl.searchParams.get("text");
    if (!encodedText) return null;
    return `whatsapp://send?text=${encodedText}`;
  } catch {
    return null;
  }
};

const openWhatsAppAppWithFallback = (whatsappUrl: string) => {
  const appUrl = buildWhatsAppAppUrl(whatsappUrl);
  const openFallback = () => {
    window.location.href = whatsappUrl;
  };

  if (!appUrl) {
    openFallback();
    return;
  }

  let appOpened = false;
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      appOpened = true;
    }
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.location.href = appUrl;

  window.setTimeout(() => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    if (!appOpened) {
      openFallback();
    }
  }, 1200);
};

export default function TrainingPlanExtension() {
  const navigate = useNavigate();
  const location = useLocation();
  const isPatientPortal = location.pathname.startsWith("/paciente/");
  const pdfRef = useRef<HTMLDivElement>(null);
  const deepLinkPlanRef = useRef<string | null>(null);
  const { exportToPDF, isExporting } = useExportToPDF(pdfRef);
  const deepLinkPlanId = useMemo(() => new URLSearchParams(location.search).get("planId"), [location.search]);
  const deepLinkPatientId = useMemo(() => new URLSearchParams(location.search).get("patientId"), [location.search]);

  const [loading, setLoading] = useState(true);
  const [professionalName, setProfessionalName] = useState<string | null>(null);
  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);

  // Wizard state
  const [step, setStep] = useState<WizardStep>("profile");

  // Profile data
  const [healthSummary, setHealthSummary] = useState("Carregando resumo de saúde...");
  const [healthScore, setHealthScore] = useState<number | null>(null);
  const [conditions, setConditions] = useState<string[]>([]);
  const [problems, setProblems] = useState<string[]>([]);
  const [activeAlerts, setActiveAlerts] = useState<ActiveAlertInfo[]>([]);
  const [medications, setMedications] = useState<string[]>([]);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [hasAnalysis, setHasAnalysis] = useState(false);

  // Objectives
  const [suggestionCards, setSuggestionCards] = useState<ObjectiveCard[]>([]);
  const [selectedObjectives, setSelectedObjectives] = useState<string[]>([]);

  // Availability
  const [availability, setAvailability] = useState<AvailabilityState>({
    daysPerWeek: 4,
    minutesPerSession: 60,
    preferredDays: [],
    period: undefined,
    location: undefined,
    observations: "",
  });

  // Plan
  const [planName, setPlanName] = useState("Plano de Treino Academia");
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<TrainingPlanHeader | null>(null);
  const [planItems, setPlanItems] = useState<TrainingSession[]>([]);
  const [generatingShare, setGeneratingShare] = useState(false);

  const selectedPatient = useMemo(
    () => patients.find((item) => item.id === selectedPatientId) || null,
    [patients, selectedPatientId],
  );

  const patientAge = calcAge(selectedPatient?.data_nascimento);

  const invokeWithHttpFallback = async <TResponse,>(
    functionName: string,
    body: Record<string, unknown>,
  ): Promise<TResponse> => {
    const { data, error } = await supabase.functions.invoke(functionName, { body });
    if (!error) return data as TResponse;

    const message = getErrorMessage(error, "Falha ao chamar função");
    const shouldTryHttp =
      message.includes(FUNCTION_FETCH_ERROR_MESSAGE) || message.includes("Failed to fetch");
    if (!shouldTryHttp) throw new Error(message);

    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("Sessão inválida para chamar a função");

    const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`;
    const response = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(body),
    });

    const rawText = await response.text();
    let parsed: Record<string, unknown> = {};
    if (rawText) {
      try {
        parsed = JSON.parse(rawText) as Record<string, unknown>;
      } catch {
        parsed = { error: rawText };
      }
    }
    if (!response.ok) {
      const err =
        typeof parsed?.error === "string"
          ? parsed.error
          : `Erro HTTP ${response.status} ao chamar ${functionName}`;
      throw new Error(err);
    }
    return parsed as TResponse;
  };

  useEffect(() => {
    void initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPatientPortal]);

  useEffect(() => {
    if (!selectedPatientId) return;
    const syncSelectedPatientData = async () => {
      await preloadPatientProfile(selectedPatientId);
      if (deepLinkPlanId && deepLinkPlanRef.current !== deepLinkPlanId) {
        deepLinkPlanRef.current = deepLinkPlanId;
        await loadPlanById(deepLinkPlanId);
        return;
      }
      await loadLatestPlanForPatient(selectedPatientId);
      setStep("profile");
    };
    void syncSelectedPatientData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatientId, deepLinkPlanId]);

  const initialize = async () => {
    try {
      setLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Faça login para acessar o plano de treino");
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .maybeSingle();
      setProfessionalName(profile?.full_name ?? null);

      const { data: patientRows, error: patientError } = await supabase
        .from("patients_onboarding")
        .select("id, full_name, cpf, email, data_nascimento")
        .eq("owner_id", user.id)
        .eq("status", "active")
        .order("full_name");

      if (patientError) throw patientError;
      const loadedPatients = (patientRows || []) as PatientOption[];
      setPatients(loadedPatients);

      if (loadedPatients.length > 0) {
        const selectedFromQuery =
          deepLinkPatientId && loadedPatients.some((item) => item.id === deepLinkPatientId)
            ? deepLinkPatientId
            : loadedPatients[0].id;
        setSelectedPatientId(selectedFromQuery);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar o aplicativo de treino");
    } finally {
      setLoading(false);
    }
  };

  const resetProfileState = () => {
    setHealthSummary("");
    setHealthScore(null);
    setConditions([]);
    setProblems([]);
    setMedications([]);
    setActiveAlerts([]);
    setSuggestionCards([]);
    setSelectedObjectives([]);
    setHasAnalysis(false);
  };

  const preloadPatientProfile = async (patientId: string) => {
    setLoadingProfile(true);
    resetProfileState();
    try {
      const [{ data: healthRecords }, { data: globalAnalysis }] = await Promise.all([
        supabase
          .from("patient_health_records")
          .select("title")
          .eq("patient_id", patientId)
          .order("created_at", { ascending: false })
          .limit(5),
        supabase
          .from("ai_global_analysis_cache")
          .select("json_result")
          .eq("patient_id", patientId)
          .maybeSingle(),
      ]);

      setConditions((healthRecords || []).map((r) => r.title).filter(Boolean));

      const insights = globalAnalysis?.json_result as
        | {
            health_score?: number;
            problems?: unknown;
            medications?: unknown;
            active_alerts?: unknown;
          }
        | null;

      if (!insights) {
        // Paciente sem análise global: não auto-gerar análise com IA.
        setHasAnalysis(false);
        return;
      }

      // Paciente com análise global: popular dados básicos e gerar automaticamente
      // o briefing do plano de treino com IA.
      setHealthScore(typeof insights.health_score === "number" ? insights.health_score : null);
      setProblems(
        Array.isArray(insights.problems)
          ? (insights.problems as unknown[]).filter((p): p is string => typeof p === "string")
          : [],
      );
      setMedications(
        Array.isArray(insights.medications)
          ? (insights.medications as unknown[]).filter((p): p is string => typeof p === "string")
          : [],
      );
      setActiveAlerts(
        Array.isArray(insights.active_alerts)
          ? (insights.active_alerts as Array<Record<string, unknown>>).map((a) => ({
              severity: typeof a.severity === "string" ? a.severity : undefined,
              title: typeof a.title === "string" ? a.title : undefined,
              message: typeof a.message === "string" ? a.message : undefined,
              source: typeof a.source === "string" ? a.source : undefined,
            }))
          : [],
      );

      await generateHealthAnalysis(patientId, { silentOnError: true });
    } catch (error: unknown) {
      console.error(error);
      toast.error(
        getErrorMessage(error, "Não foi possível carregar o perfil do paciente."),
      );
    } finally {
      setLoadingProfile(false);
    }
  };

  const generateHealthAnalysis = async (
    patientId: string,
    options: { silentOnError?: boolean } = {},
  ) => {
    setLoadingProfile(true);
    try {
      const data = await invokeWithHttpFallback<{
        healthSummary?: string;
        objectives?: Array<string | StructuredObjective>;
        structuredObjectives?: StructuredObjective[];
        problems?: string[];
        medications?: string[];
        activeAlerts?: ActiveAlertInfo[];
        highlights?: Array<{ type?: string; title?: string; message?: string }>;
        healthScore?: number | null;
      }>("generate-training-plan", {
        action: "suggest_objectives",
        patientId,
        patientid: patientId,
        frequencyPerWeek: availability.daysPerWeek,
        frequency_per_week: availability.daysPerWeek,
        weeklyMinutes: availability.daysPerWeek * availability.minutesPerSession,
        weekly_minutes: availability.daysPerWeek * availability.minutesPerSession,
      });

      if (typeof data?.healthScore === "number") {
        setHealthScore(data.healthScore);
      }

      setHealthSummary(data?.healthSummary || "Resumo sintético gerado automaticamente.");

      const structured = Array.isArray(data?.structuredObjectives)
        ? (data.structuredObjectives as StructuredObjective[])
        : normalizeObjectives(data?.objectives);

      const cards = toObjectiveCards(structured);
      setSuggestionCards(cards);
      const initial = cards.slice(0, 3).map((c) => c.title);
      setSelectedObjectives(initial);

      if (Array.isArray(data?.problems) && data.problems.length > 0) {
        setProblems(data.problems);
      }
      if (Array.isArray(data?.medications) && data.medications.length > 0) {
        setMedications(data.medications);
      }
      if (Array.isArray(data?.activeAlerts) && data.activeAlerts.length > 0) {
        setActiveAlerts(data.activeAlerts);
      }

      setHasAnalysis(true);
    } catch (error: unknown) {
      console.error(error);
      if (!options.silentOnError) {
        toast.error(
          getErrorMessage(
            error,
            "Sugestões indisponíveis no momento. Você pode seguir com objetivos manuais.",
          ),
        );
      }
      setHealthSummary("Não foi possível gerar resumo automático com IA.");
      setSuggestionCards([]);
      setHasAnalysis(false);
    } finally {
      setLoadingProfile(false);
    }
  };

  const loadPlanById = async (planId: string) => {
    try {
      const [{ data: planRow, error: planError }, { data: itemsRows, error: itemError }] = await Promise.all([
        supabase.from("training_plans").select("*").eq("id", planId).single(),
        supabase
          .from("training_plan_items")
          .select("*")
          .eq("training_plan_id", planId)
          .order("week_number", { ascending: true })
          .order("display_order", { ascending: true }),
      ]);

      if (planError) throw planError;
      if (itemError) throw itemError;

      setCurrentPlan({
        id: planRow.id,
        plan_name: planRow.plan_name,
        status: planRow.status,
        frequency_per_week: planRow.frequency_per_week,
        weekly_minutes: planRow.weekly_minutes,
        selected_objectives: asStringArray(planRow.selected_objectives),
        suggested_objectives: asStringArray(planRow.suggested_objectives),
        ai_recommendations: (planRow.ai_recommendations as Record<string, unknown> | null) ?? null,
        notes: planRow.notes,
        created_at: planRow.created_at,
      });

      setPlanItems(
        (itemsRows || []).map((item) => ({
          id: item.id,
          training_plan_id: item.training_plan_id,
          week_number: item.week_number,
          day_of_week: item.day_of_week,
          session_title: item.session_title,
          exercises: Array.isArray(item.exercises) ? (item.exercises as Array<Record<string, unknown>>) : [],
          duration_minutes: item.duration_minutes,
          intensity: item.intensity,
          notes: item.notes,
          display_order: item.display_order,
        })),
      );

      setPlanName(planRow.plan_name || "Plano de Treino Academia");
      // Sync availability from plan
      const minutesPerSession =
        planRow.frequency_per_week > 0
          ? Math.round(planRow.weekly_minutes / planRow.frequency_per_week)
          : availability.minutesPerSession;
      setAvailability((prev) => ({
        ...prev,
        daysPerWeek: planRow.frequency_per_week,
        minutesPerSession,
      }));
      setSelectedObjectives(asStringArray(planRow.selected_objectives));
      setStep("review");
    } catch (error: unknown) {
      console.error(error);
      toast.error(getErrorMessage(error, "Erro ao carregar plano"));
    }
  };

  const loadLatestPlanForPatient = async (patientId: string) => {
    const { data, error } = await supabase
      .from("training_plans")
      .select("id")
      .eq("patient_id", patientId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error(error);
      return;
    }
    if (data?.id) {
      await loadPlanById(data.id);
    } else {
      setCurrentPlan(null);
      setPlanItems([]);
      setStep("profile");
    }
  };

  const handleGeneratePlan = async () => {
    if (!selectedPatientId) {
      toast.error("Selecione um paciente");
      return;
    }
    if (!selectedObjectives.length) {
      toast.error("Selecione pelo menos um objetivo");
      return;
    }

    try {
      setGeneratingPlan(true);
      const composedNotes = formatAvailabilityForNotes(availability, selectedObjectives);
      const objectivesPayload = suggestionCards.length > 0 ? suggestionCards : selectedObjectives;
      const weeklyMinutes = availability.daysPerWeek * availability.minutesPerSession;

      const data = await invokeWithHttpFallback<{ planId?: string }>("generate-training-plan", {
        action: "generate_plan",
        patientId: selectedPatientId,
        patientid: selectedPatientId,
        planName,
        notes: composedNotes,
        frequencyPerWeek: availability.daysPerWeek,
        frequency_per_week: availability.daysPerWeek,
        weeklyMinutes,
        weekly_minutes: weeklyMinutes,
        selectedObjectives,
        selected_objectives: selectedObjectives,
        suggestedObjectives: objectivesPayload,
        suggested_objectives: objectivesPayload,
      });
      if (!data?.planId) throw new Error("Plano não retornado pela função");

      toast.success("Plano gerado com sucesso");
      await loadPlanById(data.planId);
    } catch (error: unknown) {
      console.error(error);
      toast.error(getErrorMessage(error, "Falha ao gerar plano"));
    } finally {
      setGeneratingPlan(false);
    }
  };

  const handleSavePlan = async () => {
    if (!currentPlan) return;
    try {
      setSavingPlan(true);
      const weeklyMinutes = availability.daysPerWeek * availability.minutesPerSession;
      const { error: planError } = await supabase
        .from("training_plans")
        .update({
          plan_name: planName,
          frequency_per_week: availability.daysPerWeek,
          weekly_minutes: weeklyMinutes,
          selected_objectives: selectedObjectives,
        })
        .eq("id", currentPlan.id);
      if (planError) throw planError;

      const { error: deleteError } = await supabase
        .from("training_plan_items")
        .delete()
        .eq("training_plan_id", currentPlan.id);
      if (deleteError) throw deleteError;

      if (planItems.length > 0) {
        const payload = planItems.map((item, index) => ({
          training_plan_id: currentPlan.id,
          week_number: item.week_number,
          day_of_week: item.day_of_week,
          session_title: item.session_title,
          exercises: (item.exercises ?? []) as unknown as Json,
          duration_minutes: item.duration_minutes ?? undefined,
          intensity: item.intensity ?? undefined,
          notes: item.notes ?? undefined,
          display_order: index,
        }));
        const { error: insertError } = await supabase.from("training_plan_items").insert(payload);
        if (insertError) throw insertError;
      }

      toast.success("Plano atualizado");
      await loadPlanById(currentPlan.id);
    } catch (error: unknown) {
      console.error(error);
      toast.error(getErrorMessage(error, "Erro ao salvar alterações"));
    } finally {
      setSavingPlan(false);
    }
  };

  const handleShareWhatsApp = async () => {
    if (!currentPlan) {
      toast.error("Gere ou abra um plano antes de compartilhar");
      return;
    }

    try {
      setGeneratingShare(true);
      const data = await invokeWithHttpFallback<{
        publicUrl?: string;
        whatsappUrl?: string;
        expiresAt?: string;
      }>("generate-training-plan-share-link", {
        trainingPlanId: currentPlan.id,
        expirationDays: 30,
      });

      const publicUrl = data?.publicUrl || (data?.whatsappUrl ? extractPublicUrlFromWhatsAppUrl(data.whatsappUrl) : null);
      const message = publicUrl
        ? buildTrainingPlanWhatsAppMessage(
            selectedPatient?.full_name || "Paciente",
            publicUrl,
            isPatientPortal ? null : professionalName,
          )
        : null;
      const whatsappUrl = message ? `https://wa.me/?text=${encodeURIComponent(message)}` : data?.whatsappUrl || null;

      if (whatsappUrl) {
        openWhatsAppAppWithFallback(whatsappUrl);
        toast.success("Link público gerado. Abrindo WhatsApp...");
      } else if (publicUrl) {
        await navigator.clipboard.writeText(publicUrl);
        toast.success("Link copiado para o clipboard");
      } else {
        toast.error("Link não retornado");
      }
    } catch (error: unknown) {
      console.error(error);
      toast.error(getErrorMessage(error, "Erro ao gerar link"));
    } finally {
      setGeneratingShare(false);
    }
  };

  const handleRestart = () => {
    setCurrentPlan(null);
    setPlanItems([]);
    setStep("profile");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isPatientPortal ? "/paciente/aplicativos" : "/app/aplicativos")}
        >
          <ArrowLeft className="size-5" />
        </Button>
        <div className="p-2 rounded-lg bg-primary/10 text-primary">
          <Dumbbell className="size-6" />
        </div>
        <div className="flex flex-col">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Plano de Treino</h1>
          <p className="text-sm text-muted-foreground">
            Perfil de saúde · Objetivos · Disponibilidade
          </p>
        </div>
      </div>

      <Separator />

      {!isPatientPortal && (
        <PatientSelector
          patients={patients}
          selectedPatientId={selectedPatientId}
          onSelect={setSelectedPatientId}
        />
      )}

      {!selectedPatient ? (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Nenhum paciente encontrado para este usuário.
          </CardContent>
        </Card>
      ) : step === "review" && currentPlan ? (
        <PlanReviewView
          planName={planName}
          onPlanNameChange={setPlanName}
          selectedObjectives={selectedObjectives}
          daysPerWeek={availability.daysPerWeek}
          minutesPerSession={availability.minutesPerSession}
          items={planItems}
          onItemsChange={setPlanItems}
          onSave={handleSavePlan}
          onShareWhatsApp={handleShareWhatsApp}
          onExportPDF={() =>
            exportToPDF(`plano-treino-${selectedPatient.full_name || "paciente"}.pdf`)
          }
          onRestart={handleRestart}
          onBack={() =>
            navigate(isPatientPortal ? "/paciente/aplicativos" : "/app/aplicativos")
          }
          saving={savingPlan}
          sharing={generatingShare}
          exporting={isExporting}
          patientName={selectedPatient.full_name}
        />
      ) : (
        <div className="flex flex-col gap-6">
          <TrainingPlanStepper currentStep={step} onStepClick={setStep} />

          {step === "profile" && (
            <HealthProfileStep
              patientName={selectedPatient.full_name}
              patientAge={patientAge}
              healthSummary={healthSummary}
              healthScore={healthScore}
              conditions={conditions}
              problems={problems}
              activeAlerts={activeAlerts}
              medications={medications}
              loading={loadingProfile && !hasAnalysis}
              hasAnalysis={hasAnalysis}
              onContinue={() => setStep("objectives")}
              onRegenerate={() => {
                if (!selectedPatientId) return;
                void generateHealthAnalysis(selectedPatientId);
              }}
              onGenerateAnalysis={() => {
                if (!selectedPatientId) return;
                void generateHealthAnalysis(selectedPatientId);
              }}
              regenerating={loadingProfile}
            />
          )}

          {step === "objectives" && (
            <ObjectivesStep
              suggestions={suggestionCards}
              selected={selectedObjectives}
              onChange={setSelectedObjectives}
              onBack={() => setStep("profile")}
              onContinue={() => setStep("availability")}
            />
          )}

          {step === "availability" && (
            <AvailabilityStep
              value={availability}
              onChange={setAvailability}
              onBack={() => setStep("objectives")}
              onGenerate={handleGeneratePlan}
              generating={generatingPlan}
            />
          )}
        </div>
      )}

      {/* Open existing plan shortcut when on wizard but plan exists */}
      {step !== "review" && currentPlan && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Plano existente</CardTitle>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => setStep("review")}>
              Abrir último plano gerado
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Hidden PDF template */}
      <div className="hidden">
        <div ref={pdfRef}>
          {currentPlan && selectedPatient ? (
            <PlanPDFTemplate
              planName={planName}
              patientName={selectedPatient.full_name}
              selectedObjectives={selectedObjectives}
              daysPerWeek={availability.daysPerWeek}
              minutesPerSession={availability.minutesPerSession}
              items={planItems}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
